import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listNotifications, type Notification } from '@/api/notifications';
import { fireNotification, getNotificationPermission } from '@/lib/notify';

const LAST_SEEN_KEY = 'grhamoy.notif.lastSeenId';

function readLastSeen(): bigint {
  try {
    const v = localStorage.getItem(LAST_SEEN_KEY);
    return v ? BigInt(v) : 0n;
  } catch {
    return 0n;
  }
}

function writeLastSeen(id: bigint): void {
  try {
    localStorage.setItem(LAST_SEEN_KEY, id.toString());
  } catch { /* private mode */ }
}

function toBigInt(id: string | number): bigint {
  try { return BigInt(id); } catch { return 0n; }
}

/**
 * Background poller — every 30s pulls the current user's newest unread
 * notifications and fires an OS-level popup for anything with an id higher
 * than what we already announced.
 *
 * The polled endpoint is HTTP; there is no WebSocket / SSE in this ERP yet.
 * Push works even when the tab is in the background as long as the tab
 * process is alive.
 *
 * On first load we seed the "last seen" id from the current top so we don't
 * blast the user with popups for their entire backlog.
 */
export function useOsNotifier(enabled: boolean) {
  const seededRef = useRef(false);

  const { data: items = [] } = useQuery({
    queryKey: ['notifications', 'os-poll'],
    queryFn: () => listNotifications({ limit: 20, status: 'unread' }),
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    // The list is polled purely to trigger OS pushes; we don't want it
    // fighting the bell's own list cache.
    staleTime: 0,
  });

  useEffect(() => {
    if (!enabled) return;
    if (getNotificationPermission() !== 'granted') return;
    if (items.length === 0) return;

    const lastSeen = readLastSeen();
    let newHighest = lastSeen;

    // First run in this browser: seed lastSeen with the current top so the
    // user doesn't get a wall of popups for pre-existing notifications.
    if (!seededRef.current && lastSeen === 0n) {
      const top = items.reduce<bigint>((max, n) => {
        const idBig = toBigInt(n.id);
        return idBig > max ? idBig : max;
      }, 0n);
      writeLastSeen(top);
      seededRef.current = true;
      return;
    }
    seededRef.current = true;

    // Fire in chronological order so the freshest ends up on top of the OS
    // stack. Cap at 5 pushes per tick to avoid a burst overwhelming the user.
    const fresh = items
      .filter((n) => toBigInt(n.id) > lastSeen)
      .sort((a, b) => (toBigInt(a.id) < toBigInt(b.id) ? -1 : 1))
      .slice(-5);

    fresh.forEach((n: Notification) => {
      fireNotification(n.subject ?? 'New notification', {
        body: n.body,
        tag: `notif-${n.id}`,
        href: n.actionHref ?? undefined,
      });
      const idBig = toBigInt(n.id);
      if (idBig > newHighest) newHighest = idBig;
    });

    if (newHighest > lastSeen) writeLastSeen(newHighest);
  }, [items, enabled]);
}
